import {
  LegacyPortalDashboardProductType,
  LegacyPortalDashboardService,
} from "@spwn-portal/grpc";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import React from "react";
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";
import { minBigInt } from "./minBigInt";

import type { ConnectError } from "@bufbuild/connect";

export type EventSalesDetail = {
  productType: LegacyPortalDashboardProductType;
  productName: string;
  price: number;
  quantity: number;
  uniquePurchaseUserCount: number;
};

type GetEventSalesDetailsResponse = {
  eventSalesDetails: EventSalesDetail[];
  bigqueryLastWrittenAt?: bigint;
};

type GetEventSalesDetailsError = ConnectError | { message: string };

const GetEventSalesDetailsKey = "getEventSalesDetails";

const isTicketProductType = (productType: LegacyPortalDashboardProductType) =>
  productType === LegacyPortalDashboardProductType.STREAMING_TICKET ||
  productType === LegacyPortalDashboardProductType.VENUE_TICKET;

export const useGetEventSalesDetails = (
  eventIds: string[],
  fromDate?: bigint,
  toDate?: bigint
) => {
  const auth = firebase.auth();
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const currentUserId = auth.currentUser?.uid ?? null;

  const {
    data: fetchedData,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<GetEventSalesDetailsResponse, GetEventSalesDetailsError>(
    currentUserId && eventIds.length > 0
      ? [
          GetEventSalesDetailsKey,
          currentUserId,
          [...eventIds].sort().join(","),
          fromDate?.toString() ?? "",
          toDate?.toString() ?? "",
        ]
      : null,
    async () => {
      if (!connectClient) {
        throw new Error("Connect client is not initialized");
      }
      try {
        const responses = await Promise.all(
          eventIds.map(async (eventId) =>
            connectClient.getEventSalesDetails({
              eventId,
              fromDate,
              toDate,
            })
          )
        );
        const eventSalesDetails = responses.flatMap((response) =>
          response.eventSalesDetails.map((item) => ({
            productType: item.productType,
            productName: item.productName,
            price: item.price,
            quantity: item.quantity,
            uniquePurchaseUserCount: item.uniquePurchaseUserCount,
          }))
        );
        const bigqueryLastWrittenAt = minBigInt(
          responses.map((response) => response.bigqueryLastWrittenAt)
        );
        return {
          eventSalesDetails,
          ...(bigqueryLastWrittenAt !== undefined
            ? { bigqueryLastWrittenAt }
            : {}),
        };
      } catch (error) {
        console.error("Error fetching event sales details:", error);
        throw error;
      }
    }
  );

  const { ticketSalesDetails, otherSalesDetails } = React.useMemo(() => {
    const eventSalesDetails = fetchedData?.eventSalesDetails ?? [];

    return eventSalesDetails.reduce<{
      ticketSalesDetails: EventSalesDetail[];
      otherSalesDetails: EventSalesDetail[];
    }>(
      (result, item) => {
        if (isTicketProductType(item.productType)) {
          result.ticketSalesDetails.push(item);
          return result;
        }

        result.otherSalesDetails.push(item);
        return result;
      },
      {
        ticketSalesDetails: [],
        otherSalesDetails: [],
      }
    );
  }, [fetchedData]);

  return {
    eventIds,
    isLoading,
    isValidating,
    error,
    mutate,
    fetchedData,
    ticketSalesDetails,
    otherSalesDetails,
  };
};
